import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';

import { API_URL } from '../config';

function SearchAnswers() {
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAnswers, setFilteredAnswers] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [filteredUnansweredQuestions, setFilteredUnansweredQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editText, setEditText] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [answeringQuestion, setAnsweringQuestion] = useState(null);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [allQuestions, setAllQuestions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

// Replace the filtering logic in your useEffect with this safer version:

useEffect(() => {
  // Filter both answers and questions based on search term
  if (searchTerm && searchTerm.trim()) {
    try {
      const searchLower = searchTerm.toLowerCase();
      
      // Filter answers - with null safety
      const filteredA = answers.filter(answer => {
        // Safely handle null/undefined values
        const questionText = answer.question_text ? answer.question_text.toString().toLowerCase() : '';
        const answerText = answer.answer_text ? answer.answer_text.toString().toLowerCase() : '';
        return questionText.includes(searchLower) || answerText.includes(searchLower);
      });
      
      // Filter user's own questions - with null safety
      const filteredQ = questions.filter(question => {
        const questionText = question.text ? question.text.toString().toLowerCase() : '';
        return questionText.includes(searchLower) && question.created_by_user_id === currentUserId;
      });
      
      // Filter unanswered questions from main pool
      const answeredQuestionIds = new Set(answers.map(answer => answer.question_id));
      const unansweredQuestions = allQuestions.filter(question => 
        !answeredQuestionIds.has(question.id) && 
        question.created_by_user_id !== currentUserId // Don't include user's own questions here
      );
      const filteredUnanswered = unansweredQuestions.filter(question => {
        const questionText = question.text ? question.text.toString().toLowerCase() : '';
        return questionText.includes(searchLower);
      });
      
      setFilteredAnswers(filteredA);
      setFilteredQuestions(filteredQ);
      setFilteredUnansweredQuestions(filteredUnanswered);
    } catch (err) {
      console.error('Error filtering:', err);
      setFilteredAnswers([]);
      setFilteredQuestions([]);
      setFilteredUnansweredQuestions([]);
    }
  } else {
    setFilteredAnswers(answers);
    setFilteredQuestions(questions.filter(q => q.created_by_user_id === currentUserId));
    
    // Show unanswered questions from main pool
    const answeredQuestionIds = new Set(answers.map(answer => answer.question_id));
    const unansweredQuestions = allQuestions.filter(question => 
      !answeredQuestionIds.has(question.id) && 
      question.created_by_user_id !== currentUserId
    );
    setFilteredUnansweredQuestions(unansweredQuestions);
  }
}, [searchTerm, answers, questions, allQuestions, currentUserId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      // Fetch user info, answers, and questions in parallel
      const [userResponse, answersResponse, questionsResponse, allQuestionsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/answers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/admin/questions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/questions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      setCurrentUserId(userResponse.data.id);
      
      // Clean answers data
      const rawAnswers = answersResponse.data || [];
      const cleanAnswers = rawAnswers.filter(answer => 
        answer && typeof answer === 'object' && answer.answer_id
      );
      setAnswers(cleanAnswers);
      
      // Clean questions data
      const rawQuestions = questionsResponse.data || [];
      const cleanQuestions = rawQuestions.filter(question => 
        question && typeof question === 'object' && question.id
      );
      setQuestions(cleanQuestions);
      
      // Clean all questions data
      const rawAllQuestions = allQuestionsResponse.data || [];
      const cleanAllQuestions = rawAllQuestions.filter(question => 
        question && typeof question === 'object' && question.id
      );
      setAllQuestions(cleanAllQuestions);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all answers to this question.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setQuestions(questions.filter(question => question.id !== questionId));
      showSuccess('Question deleted successfully!');
    } catch (err) {
      setError('Failed to delete question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setEditText((question.text || '').toString());
  };

  const handleSaveQuestionEdit = async () => {
    if (!editText.trim()) {
      setError('Question text cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/questions/${editingQuestion}`, 
        { text: editText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setQuestions(questions.map(question => 
        question.id === editingQuestion 
          ? { ...question, text: editText }
          : question
      ));
      
      setEditingQuestion(null);
      setEditText('');
      showSuccess('Question updated successfully!');
    } catch (err) {
      setError('Failed to update question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteAnswer = async (answerId) => {
    if (!window.confirm('Are you sure you want to delete this answer?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/answer/${answerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setAnswers(answers.filter(answer => answer.answer_id !== answerId));
      showSuccess('Answer deleted successfully!');
    } catch (err) {
      setError('Failed to delete answer: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditAnswer = (answer) => {
    setEditingAnswer(answer.answer_id);
    setEditText((answer.answer_text || '').toString());
  };

  const handleSaveAnswerEdit = async () => {
    if (!editText.trim()) {
      setError('Answer text cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/answer/${editingAnswer}`, 
        { text: editText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setAnswers(answers.map(answer => 
        answer.answer_id === editingAnswer 
          ? { ...answer, answer_text: editText }
          : answer
      ));
      
      setEditingAnswer(null);
      setEditText('');
      showSuccess('Answer updated successfully!');
    } catch (err) {
      setError('Failed to update answer: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingAnswer(null);
    setEditingQuestion(null);
    setEditText('');
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAnswerQuestion = (question) => {
    setAnsweringQuestion(question.id);
    setNewAnswerText('');
  };

  const handleSaveAnswer = async () => {
    if (!newAnswerText.trim()) {
      setError('Please enter an answer');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/answer`, 
        { questionId: answeringQuestion, text: newAnswerText },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Refresh data to show the new answer
      await fetchData();
      
      setAnsweringQuestion(null);
      setNewAnswerText('');
      showSuccess('Answer saved successfully!');
    } catch (err) {
      setError('Failed to save answer: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelAnswer = () => {
    setAnsweringQuestion(null);
    setNewAnswerText('');
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="spinner">Loading your answers...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <nav className="dashboard-nav">
        <button onClick={() => window.location.href = '/dashboard'}>Dashboard</button>
        <button onClick={() => window.location.href = '/question-of-the-day'}>Question of the Day</button>
        <button onClick={() => window.location.href = '/ai-assistant'}>AI Assistant</button>
        <button onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </nav>

      <h1>Search Your Questions & Answers</h1>
      
      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      <div className="search-section">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search questions and answers..."
          className="search-input"
          style={{ 
            width: '100%', 
            padding: '12px', 
            marginBottom: '20px',
            fontSize: '16px',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        <p>{filteredAnswers.length} answers, {filteredQuestions.length} of your custom questions, and {filteredUnansweredQuestions.length} unanswered questions shown</p>
      </div>

      <div className="content-container">
        
        {/* Your Custom Questions Section */}
        {filteredQuestions.length > 0 && (
          <div className="questions-section" style={{ marginBottom: '40px' }}>
            <h2>Your Custom Questions</h2>
            {filteredQuestions.map((question) => {
              // Check if this question has been answered
              const hasAnswer = filteredAnswers.some(answer => answer.question_id === question.id);
              
              return (
                <div key={question.id} className="question-card" style={{
                  border: '1px solid #e3f2fd',
                  padding: '20px',
                  marginBottom: '15px',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa'
                }}>
                  {editingQuestion === question.id ? (
                    <div className="edit-section">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows="3"
                        style={{ 
                          width: '100%', 
                          padding: '10px',
                          marginBottom: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <div className="edit-buttons">
                        <button onClick={handleSaveQuestionEdit} style={{ marginRight: '10px' }}>
                          Save
                        </button>
                        <button onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : answeringQuestion === question.id ? (
                    <div className="answer-section">
                      <p style={{ marginBottom: '15px', lineHeight: '1.5', fontWeight: 'bold' }}>
                        Question: {(question.text || 'No question text').toString()}
                      </p>
                      <textarea
                        value={newAnswerText}
                        onChange={(e) => setNewAnswerText(e.target.value)}
                        placeholder="Enter your answer..."
                        rows="4"
                        style={{ 
                          width: '100%', 
                          padding: '10px',
                          marginBottom: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <div className="answer-buttons">
                        <button onClick={handleSaveAnswer} style={{ marginRight: '10px', backgroundColor: '#28a745', color: 'white' }}>
                          Save Answer
                        </button>
                        <button onClick={handleCancelAnswer}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ marginBottom: '15px', lineHeight: '1.5', fontWeight: 'bold' }}>
                        Question: {(question.text || 'No question text').toString()}
                      </p>
                      <small style={{ color: '#666' }}>
                        Created: {question.created_at ? new Date(question.created_at).toLocaleDateString() : 'Unknown date'}
                        {hasAnswer && <span style={{ color: '#28a745', marginLeft: '10px' }}>✓ Answered</span>}
                      </small>
                      <div className="question-actions" style={{ marginTop: '15px' }}>
                        {!hasAnswer && (
                          <button 
                            onClick={() => handleAnswerQuestion(question)}
                            style={{ marginRight: '10px', backgroundColor: '#007bff', color: 'white' }}
                          >
                            Answer Question
                          </button>
                        )}
                        <button 
                          onClick={() => handleEditQuestion(question)}
                          style={{ marginRight: '10px' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="danger-button"
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Unanswered Questions Section */}
        {filteredUnansweredQuestions.length > 0 && (
          <div className="unanswered-questions-section" style={{ marginBottom: '40px' }}>
            <h2>Unanswered Questions</h2>
            {filteredUnansweredQuestions.map((question) => (
              <div key={question.id} className="question-card" style={{
                border: '1px solid #fff3cd',
                padding: '20px',
                marginBottom: '15px',
                borderRadius: '8px',
                backgroundColor: '#fffbf0'
              }}>
                {answeringQuestion === question.id ? (
                  <div className="answer-section">
                    <p style={{ marginBottom: '15px', lineHeight: '1.5', fontWeight: 'bold' }}>
                      Question: {(question.text || 'No question text').toString()}
                    </p>
                    <textarea
                      value={newAnswerText}
                      onChange={(e) => setNewAnswerText(e.target.value)}
                      placeholder="Enter your answer..."
                      rows="4"
                      style={{ 
                        width: '100%', 
                        padding: '10px',
                        marginBottom: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <div className="answer-buttons">
                      <button onClick={handleSaveAnswer} style={{ marginRight: '10px', backgroundColor: '#28a745', color: 'white' }}>
                        Save Answer
                      </button>
                      <button onClick={handleCancelAnswer}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ marginBottom: '15px', lineHeight: '1.5', fontWeight: 'bold' }}>
                      Question: {(question.text || 'No question text').toString()}
                    </p>
                    <small style={{ color: '#666' }}>
                      From question pool
                    </small>
                    <div className="question-actions" style={{ marginTop: '15px' }}>
                      <button 
                        onClick={() => handleAnswerQuestion(question)}
                        style={{ backgroundColor: '#007bff', color: 'white' }}
                      >
                        Answer Question
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Answers Section */}
        <div className="answers-section">
          <h2>Your Answers</h2>
          {filteredAnswers.length > 0 ? (
            filteredAnswers.map((answer) => (
              <div key={answer.answer_id} className="answer-card" style={{
                border: '1px solid #ddd',
                padding: '20px',
                marginBottom: '20px',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9'
              }}>
                <h3 style={{ color: '#333', marginBottom: '10px' }}>
                  {(answer.question_text || 'No question text').toString()}
                </h3>
                
                {editingAnswer === answer.answer_id ? (
                  <div className="edit-section">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows="4"
                      style={{ 
                        width: '100%', 
                        padding: '10px',
                        marginBottom: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <div className="edit-buttons">
                      <button onClick={handleSaveAnswerEdit} style={{ marginRight: '10px' }}>
                        Save
                      </button>
                      <button onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ marginBottom: '15px', lineHeight: '1.5' }}>
                      {(answer.answer_text || 'No answer text').toString()}
                    </p>
                    <div className="answer-meta">
                      <small style={{ color: '#666' }}>
                        Answered: {answer.created_at ? new Date(answer.created_at).toLocaleDateString() : 'Unknown date'}
                      </small>
                    </div>
                    <div className="answer-actions" style={{ marginTop: '15px' }}>
                      <button 
                        onClick={() => handleEditAnswer(answer)}
                        style={{ marginRight: '10px' }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteAnswer(answer.answer_id)}
                        className="danger-button"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            <div className="no-results">
              {searchTerm ? (
                <p>No answers found matching "{searchTerm}"</p>
              ) : (
                <p>No answers found. Start by answering some questions!</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchAnswers;